import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CalendarClock, Loader2 } from "lucide-react";
import { openCalendly } from "@/lib/calendly";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Home Services",
  "Ecommerce",
  "Real Estate",
  "Professional Services",
  "Operations-heavy Small Team",
  "Other",
];

const EMPTY = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  business_type: "",
  biggest_bottleneck: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // Anti-spam: honeypot + form-mount timestamp
  const [honeypot, setHoneypot] = useState("");
  const mountedAt = useRef(Date.now());
  useEffect(() => {
    mountedAt.current = Date.now();
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Lightweight validation
    for (const key of Object.keys(EMPTY)) {
      if (!form[key].trim()) {
        toast.error("Missing information", {
          description: `Please complete the "${labelFor(key)}" field.`,
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        website: honeypot,
        ts: mountedAt.current,
      });
      setDone(true);
      toast.success("Request received — opening your calendar", {
        description: "Pick a 45-min slot. We'll confirm by email.",
      });
      // Open Calendly with prefilled details so the user can book right away.
      openCalendly({
        name: form.name,
        email: form.email,
        customAnswers: { a1: form.business_name, a2: form.biggest_bottleneck },
      });
      setForm(EMPTY);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      toast.error("Could not submit", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      className="relative bg-zinc-950 text-white overflow-hidden"
      data-testid="booking-section"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-orange-500" />
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-orange-500 font-semibold">
                Book Your Assessment
              </span>
            </div>
            <h2 className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white">
              Book your AI Bottleneck Assessment.
            </h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              See where your business is leaking time, leads or money — and exactly what to
              do about it. We'll reply within one business day to schedule a 45-minute
              assessment call.
            </p>

            <ul className="mt-8 space-y-3 text-zinc-300 text-sm">
              {[
                "No sales deck. No retainer trap.",
                "Direct conversation with the person running the assessment.",
                "You'll leave the call with at least one thing you can fix this week.",
              ].map((x) => (
                <li key={x} className="flex gap-3">
                  <span className="font-mono text-orange-500">+</span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => openCalendly()}
              className="mt-8 inline-flex items-center gap-2 bg-orange-600 text-white px-5 py-3 text-sm font-medium border border-orange-600 hover:bg-orange-700 hover:border-orange-700 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(250,250,250,0.9)] transition-all duration-200"
              data-testid="booking-calendly-btn"
            >
              <CalendarClock size={16} />
              Open my calendar now
            </button>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Or share your context first using the form →
            </p>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <SuccessCard onAgain={() => setDone(false)} />
            ) : (
              <form
                onSubmit={onSubmit}
                className="bg-white text-zinc-950 border border-zinc-950 p-6 sm:p-10 shadow-[10px_10px_0px_0px_rgba(234,88,12,1)] reveal"
                data-testid="booking-form"
                noValidate
              >
                {/* Honeypot — hidden from humans, bots fill it. Do not remove. */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="website">Website (leave empty)</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    data-testid="form-honeypot"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Your name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Jane Doe"
                    testId="form-name"
                    autoComplete="name"
                  />
                  <Field
                    label="Business name"
                    name="business_name"
                    value={form.business_name}
                    onChange={onChange}
                    placeholder="Acme Co."
                    testId="form-business-name"
                    autoComplete="organization"
                  />
                  <Field
                    label="Work email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    testId="form-email"
                    autoComplete="email"
                  />
                  <Field
                    label="Phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+1 555 000 0000"
                    testId="form-phone"
                    autoComplete="tel"
                  />
                  <SelectField
                    label="Business type"
                    name="business_type"
                    value={form.business_type}
                    onChange={onChange}
                    options={BUSINESS_TYPES}
                    testId="form-business-type"
                  />
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="biggest_bottleneck"
                      className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500"
                    >
                      Biggest bottleneck right now
                    </label>
                    <textarea
                      id="biggest_bottleneck"
                      name="biggest_bottleneck"
                      value={form.biggest_bottleneck}
                      onChange={onChange}
                      rows={4}
                      placeholder="e.g. After-hours leads don't get replied to until next day, so we're losing jobs to faster competitors."
                      className="mt-2 w-full bg-white border border-zinc-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent transition-all resize-none"
                      data-testid="form-bottleneck"
                    />
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-[0.16em]">
                    No spam · No drip sequence
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-6 py-4 text-base font-medium border border-zinc-950 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(234,88,12,1)] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="form-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Book my assessment
                        <ArrowRight
                          size={18}
                          className="transition-transform group-hover:translate-x-1"
                        />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", testId, autoComplete }) {
  return (
    <div>
      <label
        htmlFor={name}
        className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500"
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="mt-2 w-full bg-white border border-zinc-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent transition-all"
        data-testid={testId}
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, testId }) {
  return (
    <div>
      <label
        htmlFor={name}
        className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500"
      >
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className="mt-2 w-full bg-white border border-zinc-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent transition-all appearance-none"
        data-testid={testId}
      >
        <option value="" disabled>
          Select a business type…
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function SuccessCard({ onAgain }) {
  return (
    <div
      className="bg-white text-zinc-950 border border-zinc-950 p-8 sm:p-12 shadow-[10px_10px_0px_0px_rgba(234,88,12,1)]"
      data-testid="booking-success"
    >
      <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-orange-600">
        Request received · 200 OK
      </div>
      <h3 className="mt-4 font-display font-bold text-3xl sm:text-4xl tracking-tight">
        Got it. Pick a time that works.
      </h3>
      <p className="mt-4 text-zinc-600 leading-relaxed">
        Your calendar just opened in a new layer — grab a 45-minute slot. If you closed it,
        no worries: re-open it below. We'll also reply by email within one business day.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={() => openCalendly()}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-3 text-sm font-medium border border-zinc-950 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(234,88,12,1)] transition-colors"
          data-testid="booking-success-calendar"
        >
          <CalendarClock size={16} /> Open my calendar
        </button>
        <button
          type="button"
          onClick={onAgain}
          className="inline-flex items-center gap-2 bg-white text-zinc-950 px-5 py-3 text-sm font-medium border border-zinc-300 hover:border-zinc-950 transition-colors"
          data-testid="booking-submit-another"
        >
          Submit another request
        </button>
      </div>
    </div>
  );
}

function labelFor(key) {
  return {
    name: "Your name",
    business_name: "Business name",
    email: "Work email",
    phone: "Phone",
    business_type: "Business type",
    biggest_bottleneck: "Biggest bottleneck right now",
  }[key];
}
